import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ArrowForwardIos } from '@material-ui/icons';

import { routes } from '@config/routes';
import { createRouteUrl } from '@lib/routing';
import { WidgetsBlock } from '@modules/layout/moleculas';
import { DashboardWidgetCount } from '@modules/dashboard/organisms';
import {
    useSummaryFiltersQuery,
    useSummaryLapsOnlineStudentsResults,
} from '@modules/summary-results/hooks';
import { SummaryResultsFilterRequest } from '@modules/summary-results/requests';

type SummaryStudentsWidgetsProps = {
    canSchools: boolean;
    canCompleteStudents: boolean;
    canOutStudents: boolean;
};

const Root = styled(WidgetsBlock)`
    & > div:last-child {
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }
`;

const forwardToIcon = (
    <ArrowForwardIos
        style={{
            marginTop: 2,
            marginInlineEnd: 2,
            fontSize: 24,
        }}
    />
);

const SummaryStudentsWidgets = (props: SummaryStudentsWidgetsProps): React.ReactElement => {
    const { canSchools, canCompleteStudents, canOutStudents } = props;

    const { t } = useTranslation(['summary']);

    const { filters } = useSummaryFiltersQuery();
    const filter = new SummaryResultsFilterRequest(filters);

    const { summaryLapsOnlineStudentsResults, loading } = useSummaryLapsOnlineStudentsResults({
        fetchPolicy: 'network-only',
        variables: { filter },
    });

    const routePayload = { query: filter };

    return (
        <Root title={t('summary:students')}>
            {canSchools ? (
                <DashboardWidgetCount
                    label={t('summary:schools')}
                    href={createRouteUrl(routes.summarySchools.path, routePayload)}
                    linkState={filters}
                    count={summaryLapsOnlineStudentsResults.schoolsCount}
                    loading={loading}
                    additionalCountChild={forwardToIcon}
                />
            ) : null}

            {canCompleteStudents ? (
                <DashboardWidgetCount
                    label={t('summary:students_with_out')}
                    href={createRouteUrl(routes.summaryStudents.path, {
                        query: { ...routePayload.query, isCompleted: true },
                    })}
                    linkState={filters}
                    count={summaryLapsOnlineStudentsResults.completedStudentsCount}
                    loading={loading}
                    additionalCountChild={forwardToIcon}
                />
            ) : null}

            {canOutStudents ? (
                <DashboardWidgetCount
                    label={t('summary:students_out')}
                    href={createRouteUrl(routes.summaryStudents.path, {
                        query: { ...routePayload.query, isCompleted: false },
                    })}
                    linkState={filters}
                    count={summaryLapsOnlineStudentsResults.incompletedStudentsCount}
                    loading={loading}
                    additionalCountChild={forwardToIcon}
                />
            ) : null}
        </Root>
    );
};

export { SummaryStudentsWidgets };
